<template>

<Fancybox :options="{Toolbar: {display: {right: ['close']}}}"></Fancybox>

<div v-if="showPupilSelector">
	<PupilSelector @select="handlePupilSelection" @requestClose="hideEditor" />
</div>


<Drawer v-else ref="drawer" initialWidth="2xl" @requestUnmount="hideEditor" :showDefaultCloseButton="false" :beforeClose="canClose" >

	<template #header>
		<div v-if="editableModel" class="font-semibold text-md w-full flex flex-col items-center">
			<div class="w-full flex justify-between">
				{{ $t('Observe and evaluate') }}
				<!-- Teacher <-> Pupil interface toggle -->
				<div class="bg-base-200 border border-base-300 rounded-full p-0.5 flex items-center gap-1 mr-6">
					<button type="button" class="rounded-full w-7 h-7 grid place-content-center" :class="[showTeacherInterface ? 'bg-base' : '']" @click="showInterface('teacher')">
						<div class="tooltip tooltip-bottom" :data-tip="$t('Teacher')">
							<UserIcon class="w-5 h-5 place-self-center"></UserIcon>
						</div>
					</button>
					<button type="button" class="rounded-full w-7 h-7 grid place-content-center" :class="[showPupilInterface? 'bg-base' : '']" @click="showInterface('pupil')">
						<div class="tooltip tooltip-bottom" :data-tip="$t('Pupil')">
							<UserIcon class="w-3.5 h-3.5 place-self-center"></UserIcon>
						</div>
					</button>
				</div>
			</div>
			<div class="flex gap-4 items-center justify-center py-1 rounded-xl" v-if="editableModel">
				<PupilSymbol :pupil="editableModel.pupil" class="w-12" />
				<span class="font-bold text-lg">{{ editableModel.pupil.getFullName() }}</span>
			</div>
		</div>
	</template>



	<template #default="{ hide }">

		<div v-if="!editableModel" class="flex justify-center">
			<span class="loading loading-spinner loading-sm"></span>
		</div>

		<form @submit.prevent="save" class="p-6 flex flex-col space-y-10" v-if="editableModel">

			<div>

				<div v-if="canSelectActivity">

					<div class="text-xs uppercase pt-2 font-semibold">{{ $t('Observed lesson or activity') }}</div>

					<div class="flex flex-col gap-2">

						<div v-if="showTeacherInterface" class="grid grid-cols-12 gap-2">
							<div class="col-span-12 sm:col-span-3">
								<DateSelector
									v-model="dateForLinkedLesson"
									input-css-classes=""
									:year-range="[new Date().getFullYear() - 1, new Date().getFullYear()]"
									:max-date="new Date()"
									:clearable="false"
									autocomplete="off"
									spellcheck="false"
								/>
							</div>
							<div class="hidden sm:flex justify-center items-center">
								<ChevronRightIcon class="w-5 h-5 text-base-content-light" />
							</div>
							<div class="col-span-12 sm:col-span-8">
								<MultiSelect
									:values="relatedEventOptions"
									:selectedIds="[editableModel.relatedActivity?.id]"
									:selectedTypes="[editableModel.relatedActivity ? typeof editableModel.relatedActivity : null]"
									:withFilter="false"
									:singleModus="true"
									@itemSelected="selectActivity"
									@itemUnselected="selectActivity(null)"
									buttonFieldClasses="border border-base-300"
								>
									<!-- currently selected value representation -->
									<div v-if="relatedEventOptionsLoading">
										<span class="loading loading-spinner loading-md"></span>
									</div>

									<div v-else-if="isPupilActivity(editableModel.relatedActivity)" class="text-base-content flex items-center gap-4">
										<ActivitySymbol :activity="editableModel.relatedActivity.lessonItem" class="w-10" />
										<span>{{ editableModel.pupilActivity.activity.name }}</span>
									</div>
									<div v-else-if="isLesson(editableModel.relatedActivity)" class="text-base-content flex items-center gap-4">
										<!-- icon classroom -->
										<svg v-if="isLesson(editableModel.relatedActivity)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" class="w-7">
											<path d="M116 416h-40C34.13 416 0 451.9 0 496C0 504.8 7.25 512 16 512S32 504.8 32 496C32 469.5 51.75 448 76 448h40C140.3 448 160 469.5 160 496C160 504.8 167.3 512 176 512S192 504.8 192 496C192 451.9 157.9 416 116 416zM564 416h-40C482.1 416 448 451.9 448 496c0 8.75 7.25 16 16 16s16-7.25 16-16c0-26.5 19.75-48 44-48h40C588.3 448 608 469.5 608 496c0 8.75 7.25 16 16 16s16-7.25 16-16C640 451.9 605.9 416 564 416zM340 416h-40C258.1 416 224 451.9 224 496C224 504.8 231.3 512 240 512S256 504.8 256 496C256 469.5 275.8 448 300 448h40C364.3 448 384 469.5 384 496c0 8.75 7.25 16 16 16s16-7.25 16-16C416 451.9 381.9 416 340 416zM256.2 320c0 35.35 28.44 63.98 63.79 63.98S383.8 355.3 383.8 320S355.3 256 320 256S256.2 284.7 256.2 320zM352 320c0 17.64-14.36 32-32 32s-32-14.36-32-32s14.36-32 32-32S352 302.4 352 320zM544 256c-35.35 0-63.79 28.64-63.79 63.98S508.7 383.1 544 383.1S607.8 355.3 607.8 320S579.3 256 544 256zM544 352c-17.64 0-32-14.36-32-32s14.36-32 32-32s32 14.36 32 32S561.6 352 544 352zM96 383.1c35.35 0 63.79-28.64 63.79-63.98S131.3 256 96 256S32.21 284.7 32.21 320S60.65 383.1 96 383.1zM96 288c17.64 0 32 14.36 32 32s-14.36 32-32 32s-32-14.36-32-32S78.36 288 96 288zM48 240C56.84 240 64 232.8 64 224V48C64 39.17 71.19 32 80 32h480C568.8 32 576 39.17 576 48V224c0 8.844 7.156 16 16 16S608 232.8 608 224V48c0-26.47-21.53-47.1-48-47.1h-480C53.53 .0001 32 21.53 32 48V224C32 232.8 39.16 240 48 240z"/>
										</svg>
										<span>{{ editableModel.lesson.title }}</span>
									</div>
									<div v-else-if="isLessonItem(editableModel.relatedActivity)" class="text-base-content flex items-center gap-4">
										<svg v-if="isLessonItem(editableModel.relatedActivity)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" class="w-7">
											<path d="M116 416h-40C34.13 416 0 451.9 0 496C0 504.8 7.25 512 16 512S32 504.8 32 496C32 469.5 51.75 448 76 448h40C140.3 448 160 469.5 160 496C160 504.8 167.3 512 176 512S192 504.8 192 496C192 451.9 157.9 416 116 416zM564 416h-40C482.1 416 448 451.9 448 496c0 8.75 7.25 16 16 16s16-7.25 16-16c0-26.5 19.75-48 44-48h40C588.3 448 608 469.5 608 496c0 8.75 7.25 16 16 16s16-7.25 16-16C640 451.9 605.9 416 564 416zM340 416h-40C258.1 416 224 451.9 224 496C224 504.8 231.3 512 240 512S256 504.8 256 496C256 469.5 275.8 448 300 448h40C364.3 448 384 469.5 384 496c0 8.75 7.25 16 16 16s16-7.25 16-16C416 451.9 381.9 416 340 416zM256.2 320c0 35.35 28.44 63.98 63.79 63.98S383.8 355.3 383.8 320S355.3 256 320 256S256.2 284.7 256.2 320zM352 320c0 17.64-14.36 32-32 32s-32-14.36-32-32s14.36-32 32-32S352 302.4 352 320zM544 256c-35.35 0-63.79 28.64-63.79 63.98S508.7 383.1 544 383.1S607.8 355.3 607.8 320S579.3 256 544 256zM544 352c-17.64 0-32-14.36-32-32s14.36-32 32-32s32 14.36 32 32S561.6 352 544 352zM96 383.1c35.35 0 63.79-28.64 63.79-63.98S131.3 256 96 256S32.21 284.7 32.21 320S60.65 383.1 96 383.1zM96 288c17.64 0 32 14.36 32 32s-14.36 32-32 32s-32-14.36-32-32S78.36 288 96 288zM48 240C56.84 240 64 232.8 64 224V48C64 39.17 71.19 32 80 32h480C568.8 32 576 39.17 576 48V224c0 8.844 7.156 16 16 16S608 232.8 608 224V48c0-26.47-21.53-47.1-48-47.1h-480C53.53 .0001 32 21.53 32 48V224C32 232.8 39.16 240 48 240z"/>
										</svg>
										<span>{{ editableModel.lessonItem?.lessonPlan?.title }}</span>
									</div>
									<span v-else>
										{{ $t('General observation') }}
									</span>

									<!-- option representation -->
									<template #item="{ selected, value }">
										<div class="w-full flex py-2 pl-3 hover:text-accent-content hover:bg-accent">
											<div class="flex-shrink-0 flex items-center justify-center w-16">
												<ActivitySymbol v-if="isPupilActivity(value)" :activity="value.lessonItem" class="w-10" />
												<!-- icon classroom -->
												<svg v-if="isLesson(value) || isLessonItem(value)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" class="w-7">
													<path d="M116 416h-40C34.13 416 0 451.9 0 496C0 504.8 7.25 512 16 512S32 504.8 32 496C32 469.5 51.75 448 76 448h40C140.3 448 160 469.5 160 496C160 504.8 167.3 512 176 512S192 504.8 192 496C192 451.9 157.9 416 116 416zM564 416h-40C482.1 416 448 451.9 448 496c0 8.75 7.25 16 16 16s16-7.25 16-16c0-26.5 19.75-48 44-48h40C588.3 448 608 469.5 608 496c0 8.75 7.25 16 16 16s16-7.25 16-16C640 451.9 605.9 416 564 416zM340 416h-40C258.1 416 224 451.9 224 496C224 504.8 231.3 512 240 512S256 504.8 256 496C256 469.5 275.8 448 300 448h40C364.3 448 384 469.5 384 496c0 8.75 7.25 16 16 16s16-7.25 16-16C416 451.9 381.9 416 340 416zM256.2 320c0 35.35 28.44 63.98 63.79 63.98S383.8 355.3 383.8 320S355.3 256 320 256S256.2 284.7 256.2 320zM352 320c0 17.64-14.36 32-32 32s-32-14.36-32-32s14.36-32 32-32S352 302.4 352 320zM544 256c-35.35 0-63.79 28.64-63.79 63.98S508.7 383.1 544 383.1S607.8 355.3 607.8 320S579.3 256 544 256zM544 352c-17.64 0-32-14.36-32-32s14.36-32 32-32s32 14.36 32 32S561.6 352 544 352zM96 383.1c35.35 0 63.79-28.64 63.79-63.98S131.3 256 96 256S32.21 284.7 32.21 320S60.65 383.1 96 383.1zM96 288c17.64 0 32 14.36 32 32s-14.36 32-32 32s-32-14.36-32-32S78.36 288 96 288zM48 240C56.84 240 64 232.8 64 224V48C64 39.17 71.19 32 80 32h480C568.8 32 576 39.17 576 48V224c0 8.844 7.156 16 16 16S608 232.8 608 224V48c0-26.47-21.53-47.1-48-47.1h-480C53.53 .0001 32 21.53 32 48V224C32 232.8 39.16 240 48 240z"/>
												</svg>
											</div>
											<div class="flex flex-1 items-center justify-between">
												<div class="flex-1 flex-col overflow-hidden pl-4 pr-12 py-2">
													<template v-if="isPupilActivity(value)">
														<p class="opacity-40 text-xs">{{ localTime(value.checkin) }}</p>
														<span v-text="value.activity.name"></span>
													</template>
													<template v-if="isLessonItem(value)">
														<p class="opacity-40 text-xs">{{ localTime(value.lesson?.start) }}</p>
														<span v-if="value.lesson?.title" class="opacity-40">{{ value.lesson.title }} » </span>
														<span v-text="value.lessonPlan?.title"></span>
													</template>
													<template v-else-if="isLesson(value)">
														<p class="opacity-40 text-xs">{{ localTime(value.start) }}</p>
														<span v-text="value.title"></span>
													</template>
													<template v-else>
														<span v-text="value.title"></span>
													</template>
												</div>
												<div class="flex-shrink-0">
													<span v-show="selected" class="text-accent-content absolute inset-y-0 right-0 flex items-center pr-4">
														<CheckIcon class="h-7 w-7"></CheckIcon>
													</span>
												</div>
											</div>
										</div>
									</template>
								</MultiSelect>
							</div>
						</div>

						<!-- Pupil interface -->
						<div v-else-if="showPupilInterface" class="w-full overflow-hidden">
							<div v-if="pupilActivities.length === 0">
								<div class="text-base-content-light italic text-center py-2 text-sm">
									{{ $t("No free play activities were registered for this pupil today through the activity board.") }}
								</div>
							</div>
							<div v-else class="w-full flex flex-nowrap py-5 overflow-x-scroll gap-1 px-3 justify-center">
								<div v-for="pupilActivity in pupilActivities.slice().reverse()" class="relative bg-base-100 rounded-md">
									<!-- TODO add feedback icon to activity symbol if available -->
									<div v-if="isSelectedActivity(pupilActivity)" class="absolute z-10 bg-accent-focus rounded-full w-6 h-6 -top-2 -left-2 flex items-center justify-center">
										<CheckIcon class="w-4 h-4 text-white"></CheckIcon>
									</div>
									<ActivitySymbol
										v-if="pupilActivity.lessonItem"
										:activity="pupilActivity.lessonItem"
										:selected="false"
										class="w-20 rounded-lg ring-accent"
										:class="[ isSelectedActivity(pupilActivity)? 'ring-2' : ''  ]"
										@click="selectActivity(pupilActivity)"
									/>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- in case of non-editable lesson / activity (when observation editor was called via a lesson or activity) -->
				<div v-if="!canSelectActivity && editableModel.relatedActivity">

					<div class="text-xs uppercase pt-2 font-semibold">{{ $t('Observed lesson or activity') }}</div>

					<div class="flex items-center">

						<!-- 1. Is it an activity the pupil has chosen on the activity board? Show the activity icon/name -->
						<template v-if="isPupilActivity(editableModel.relatedActivity)">
							<ActivitySymbol
								v-if="editableModel.pupilActivity.lessonItem"
								:activity="editableModel.pupilActivity.lessonItem"
								:selected="false"
								class="w-20"
							/>
							<span v-text="editableModel.pupilActivity.lessonItem.activity.name"></span>
						</template>

						<!-- 2. Is it a lesson item (a lesson plan)? Show lesson plan title -->
						<template v-else-if="isLessonItem(editableModel.relatedActivity)">
							<div v-text="editableModel.lessonItem?.lessonPlan?.title"></div>
						</template>

						<!-- 3. Is it a lesson? Show lesson title -->
						<template v-else-if="isLesson(editableModel.relatedActivity)">
							<div v-text="editableModel.lesson.name"></div>
						</template>
					</div>
				</div>

				<div v-else-if="showTeacherInterface && relatedGoals.length" class="grid grid-cols-12 gap-2">
					<div class="col-start-5 col-span-full flex items-center justify-between gap-2 bg-info rounded mt-3 p-2">
						<div class="flex items-center gap-2">
							<MapPinIcon class="w-4 h-4 text-info-content" />
							<div v-html="$tc('{count} goals were covered', { count: relatedGoals.length, styledCounter: ''+relatedGoals.length+''} )"></div>
						</div>
						<div class="text-info-content/40 text-sm">{{ $t('Evaluate after saving') }}</div>
					</div>
				</div>
			</div>

			<div class="flex flex-col gap-6">
				<div class="text-xs uppercase pt-2 font-semibold border-b">{{ $t('Observation data') }}</div>

				<!-- image uploads -->
				<div class="px-2">
					<Dropzone @files-dropped="filesDropped($event)">

						<div class="flex items-start gap-2">
							<Attachments
								v-model="editableModel.files"
								v-model:uploading="attachmentsBusy"
								:preview="true"
								:addPhotoButton="showTeacherInterface"
								ref="attachmentsContainer"
								accept="image/*"
								placeholder-text=""
							/>
						</div>

						<div v-if="showPupilInterface" class="mt-2 mb-2 pr-4 flex justify-end relative">
							<button type="button" class="btn btn-primary btn-circle btn-lg z-10" @click="$refs.attachmentsContainer.openCamera()">
								<CameraIcon class="w-8 h-8"></CameraIcon>
							</button>
							<div v-if="!editableModel?.files?.length" class="btn-circle bg-warning absolute top-0 btn-lg floating-btn-pulsate"></div>
						</div>

					</Dropzone>
				</div>
				<div v-if="showTeacherInterface" class="px-2">
					<sms-textarea v-model="editableModel.note" class="h-28" autofocus :validationError="observationsStore.errors?.fields.note?.toLocaleString($i18n)" :placeholder="$t('Note')" />
				</div>
			</div>

			<!-- drawer action bar -->
			<div class="drawer-action-bar" :class="[inUpdateMode? 'justify-between' : 'justify-end']">
				<Dropdown v-if="inUpdateMode" position="top-start">
					<template #trigger>
						<button type="button" class="btn" :class="[saving? 'btn-disabled':'btn-danger btn-outline']">
							<span v-show="deleting" class="loading loading-spinner"></span>
							<TrashIcon class="w-5 h-5" />
						</button>
					</template>
					<template #content>
						<DropdownDialogConfirm :confirm-label="$t('Yes')" :cancel-label="$t('No')" confirm-style="danger" class="w-80" @confirm="confirmRemove()">
							<span class="text-danger">{{ $t('Are you sure?') }}</span>
						</DropdownDialogConfirm>
					</template>
				</Dropdown>

				<div class="flex justify-end gap-1">
					<button type="button" class="btn btn-ghost" @click="hide" :class="[saving || deleting? 'hidden':'']">
						{{ $t('Cancel') }}
					</button>
					<button type="submit" class="btn" :class="[!canSave? 'btn-disabled':'btn-primary']">
						<span v-show="saving" class="loading loading-spinner"></span>
						<HandThumbUpIcon v-if="showPupilInterface" class="w-6 h-6" />
						<label v-else>{{ $t('Save') }}</label>
					</button>
				</div>
			</div>

		</form>
	</template>
</Drawer>

<PupilGoalEvaluation
	v-if="showPupilEvaluationEditor"
	:pupil="editableModel.pupil"
	:goals="relatedGoals"
	@requestClose="showPupilEvaluationEditor = false"
></PupilGoalEvaluation>

</template>


<style>
div.related-goals-note strong {
	@apply inline-block font-bold bg-info-content text-white px-2 rounded
}
</style>


<script lang="ts">
import { mapStores, mapState } from 'pinia';
import { useObservationsStore } from '@/stores/Observations.store';
import { usePupilsStore } from '@/stores/Pupils.store';
import { usePupilActivitiesStore } from "@/stores/PupilActivities.store";
import { Observation } from '@/models/Observation.model';
import { Pupil } from '@/models/Pupil.model';
import { Lesson } from '@/models/Lesson.model';

import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag.ts";
import { Files } from "@/utils/Files.util";

import Drawer from "@/components/ui/Drawer.v2.vue";
import Dropdown from "@/components/ui/Dropdown.vue";
import DropdownDialogConfirm from "@/components/ui/DropdownDialogConfirm.vue";
import ActivitySymbol from './ui/symbols/ActivitySymbol.vue';
import PupilSymbol from './ui/symbols/PupilSymbol.vue';
import Switch from "@/components/ui/Switch.vue";
import PupilSelector from '@/components/PupilSelector.vue';
import { TrashIcon, CameraIcon, PhotoIcon, CheckIcon, UserIcon, ChevronRightIcon, PlusIcon, EllipsisHorizontalIcon, HandThumbUpIcon } from '@heroicons/vue/24/outline';
import { MapPinIcon } from '@heroicons/vue/24/solid';
import {PupilActivity} from "@/models/PupilActivity.model";
import FilePreview from "./FilePreview.vue";
import Attachments from "@/components/Attachments.vue";
import Dropzone from "@/components/Dropzone.vue";
import MultiSelect from '@/components/ui/MultiSelect.vue';
import { DateTime } from "luxon";
import DateSelector from "@/components/ui/DateSelector.vue";
import Fancybox from "./ui/Fancybox.vue";
import GoalTitle from '@/components/GoalTitle.vue';
import { Goal } from '@/models/Goal.model';
import { useLessonsStore } from '@/stores/Lessons.store';
import { LessonItem } from '@/models/LessonItem.model';
import PupilGoalEvaluation from "@/components/PupilGoalEvaluation.vue";
import SmaSyLocalStorage from "@/utils/SmaSyLocalStorage.util";
import { useUsersStore } from '@/stores/Users.store';
import { useOrganisationsStore } from '@/stores/Organisations.store';
import {UnsavedChanges} from "@/utils/UnsavedChanges.util.ts";
import Modal from "@/components/ui/Modal.vue";

function getDateFromObject(object: Lesson | LessonItem | PupilActivity) {
	if (object instanceof Lesson) {
		return object.start;
	} else if (object instanceof LessonItem) {
		return object.lesson?.start;
	} else if (object instanceof PupilActivity) {
		return object.checkin;
	}
}

export default {

	emits: ['requestClose'],

	props: {
		observation: {
			type: Observation,
			required: false,
		},
		pupil: {
            type: Pupil,
			required: false,
        },
		pupilActivity: {
			required: false
		},
		readOnly: {
			type: Boolean,
			default: false
		}
	},

	components: {
		Modal,
		Attachments,
		FilePreview,
		Drawer,
		Dropdown,
		DropdownDialogConfirm,
		ActivitySymbol,
		PupilSymbol,
		Switch,
		PupilSelector,
		TrashIcon, CameraIcon, PhotoIcon, CheckIcon, UserIcon, ChevronRightIcon, PlusIcon, EllipsisHorizontalIcon, HandThumbUpIcon, MapPinIcon,
		Dropzone,
		GoalTitle,
		MultiSelect,
		DateSelector,
		Fancybox,
		PupilGoalEvaluation,
	},

	data() {
		return {
			saving: false,
			deleting: false,
			savedObservation: null as Observation | null,
			originalModel: null as Observation | null,
			editableModel: null as Observation | null,
			selectedPupil: null as Pupil | null,
			cachedSelectedActivity: null,
			attachmentsBusy: false,
			loadingRelatedGoals: false,
			evaluatedGoal: null as Goal | null,
			evaluatedPupil: null as Pupil | null,
			showPupilInterface: false,
			dateForLinkedLesson: new Date(),
			relatedEventOptionsLoading: false,
			showPupilEvaluationEditor: false,
		}
	},

	mounted() {
		const interfaceMode = SmaSyLocalStorage.getUserItem(this.me.id, 'observation_interface_mode') ?? 'teacher';
		this.showInterface(interfaceMode);
	},

  	computed: {
		...mapState(useUsersStore, ['me']),

    	...mapStores(
			useObservationsStore,
			usePupilsStore,
			usePupilActivitiesStore,
			useLessonsStore,
			useOrganisationsStore,
		),

		inUpdateMode() {
			return (this.observation && this.observation.id)? true : false;
		},

		showTeacherInterface() {
			return !this.showPupilInterface;
		},

		showPupilSelector() {
			return !this.inUpdateMode && (!this.editableModel || !this.editableModel.pupil);
		},

		pupilActivities() {
			if(!this.editableModel?.pupil) {
				return [];
			}
            return this.pupilActivitiesStore.getActivityForPupilOnDate(this.dateForLinkedLesson, this.editableModel.pupil);
        },

		relatedEventOptions() {
			if(!this.editableModel?.pupil) {
				return [];
			}

			const options: (Lesson | LessonItem | PupilActivity)[] = [];

			this.pupilActivitiesStore.getActivityForPupilOnDate(this.dateForLinkedLesson, this.editableModel.pupil)
				.forEach((pupilActivity: PupilActivity) => {
					options.push(pupilActivity);
				});

			// add lessons to the list of options
			const lessons = this.lessonsStore.getBetweenForPupil(
				DateTime.fromJSDate(this.dateForLinkedLesson).startOf('day').toJSDate(),
				DateTime.fromJSDate(this.dateForLinkedLesson).endOf('day').toJSDate(),
				this.editableModel.pupil
			);

			lessons.forEach((lesson: Lesson) => {

				if (lesson.hasActivityBoard()) {
					return;
				}

				if (lesson.lessonItems.length === 0) {
					options.push(lesson);
					return;
				}

				lesson.lessonItems.forEach((lessonItem: LessonItem) => {
					if (lessonItem.lessonPlan && lessonItem.isPupilSelected(this.editableModel.pupil)) {
						options.push(lessonItem);
					}
				});

			});

			// sort the array of mixed objects
			options.sort((a, b) => {
				const aDate = getDateFromObject(a);
				const bDate = getDateFromObject(b);

				if (!aDate || !bDate) {
					return 0;
				}
				return aDate.getTime() - bDate.getTime();
			});

			// add 'None' option
			const noLinkOption = {
				title: this.$t('General observation'),
				id: null,
				start: null,
				end: null,
			};
			options.unshift(noLinkOption);

			return options;
		},

		canSelectActivity() {
			// Is the activity defined by the parent component?
			if (this.pupilActivity) {
				return false;
			}

			return !this.inUpdateMode;
		},

		canSave() {
			// Has any input?
			if (!this.editableModel.isValid()) {
				return false;
			}

			if (!this.saving && !this.deleting && !this.attachmentsBusy) {
				return true;
			}
		},

		/**
		 * The goals the teacher may evaluate after saving: those of the observed
		 * lesson/activity that belong to the school's curriculum. Empty without a curriculum,
		 * so neither the banner nor the evaluation drawer appears.
		 */
		relatedGoals(): Goal[] {

			if (!this.editableModel) {
				return [];
			}

			return this.organisationsStore.curriculumGoals(this.editableModel.relatedGoals);
		},
	},

	async created() {
		if(this.inUpdateMode) {

			this.originalModel = this.observation;

			// clone the store model to allow for manipulation without instantly affecting affected the store
			this.editableModel = await this.observationsStore.loadCompletely(this.observation.clone());

			this.selectedPupil = this.observation.pupil;

			UnsavedChanges.observeModel(this.editableModel.clone(), this.editableModel);

		} else if(this.pupil && this.pupilActivity) {

			this.editableModel = this.observationsStore.new(this.pupil);
			this.selectedPupil = this.pupil;
			this.editableModel.relatedActivity = this.pupilActivity;

			UnsavedChanges.observeModel(this.editableModel.clone(), this.editableModel);

		} else if(this.pupil) {

			await this.handlePupilSelection(this.pupil);

		}
	},

	watch: {
		async dateForLinkedLesson(newValue, oldValue) {
			if (newValue.toDateString() !== oldValue.toDateString()) {
				this.relatedEventOptionsLoading = true;
				await this.loadPupilActivities();
				this.editableModel.relatedActivity = null;
				this.relatedEventOptionsLoading = false;
			}
		}
	},

  	methods: {

		showInterface(mode: string){
			if(mode === 'pupil'){
				SmaSyLocalStorage.setUserItem(this.me.id, 'observation_interface_mode', "pupil");
				this.showPupilInterface = true;
			} else {
				SmaSyLocalStorage.setUserItem(this.me.id, 'observation_interface_mode', "teacher");
				this.showPupilInterface = false;
			}
		},

		isPupilActivity(object) {
			return object instanceof PupilActivity;
		},

		isLesson(object) {
			return object instanceof Lesson;
		},

		isLessonItem(object) {
			return object instanceof LessonItem;
		},

		localTime(date: Date) {
			if (!date) {
				return 'unknown';
			}

			// return date.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' });
			return date.toLocaleString(DateTime.TIME_SIMPLE);
		},

		async handlePupilSelection(pupil) {

			this.editableModel = this.observationsStore.new(pupil);

			UnsavedChanges.observeModel(this.observationsStore.new(pupil), this.editableModel);

			this.selectedPupil = pupil;
			await this.loadPupilActivities();
		},

		async loadPupilActivities() {
			await Promise.all([
				this.pupilActivitiesStore.loadActivityOnDate(this.dateForLinkedLesson),
				this.lessonsStore.loadBetween(
					DateTime.fromJSDate(this.dateForLinkedLesson).startOf('day').toJSDate(),
					DateTime.fromJSDate(this.dateForLinkedLesson).endOf('day').toJSDate()
				)
			]);

			this.selectMostRecentActivity();
		},

		isSelectedActivity(pupilActivity: PupilActivity | Lesson | LessonItem) {

			const id = pupilActivity.id;

			if (pupilActivity instanceof LessonItem) {
				return this.editableModel.relatedActivity instanceof LessonItem && this.editableModel.relatedActivity.id === id;
			}

			if (pupilActivity instanceof Lesson) {
				return this.editableModel.relatedActivity instanceof Lesson && this.editableModel.relatedActivity.id === id;
			}

			if (pupilActivity instanceof PupilActivity) {
				return this.editableModel.relatedActivity instanceof PupilActivity && this.editableModel.relatedActivity.id === id;
			}

			return false;
		},

		selectMostRecentActivity() {
            // const pupilActivities = this.pupilActivitiesStore.getActivityForPupilOnDate(new Date(), this.pupil);
            if (this.relatedEventOptions.length === 0) {
                return;
            }

			// Find the most recent activity that has already started.
			const now = new Date();
			const recentActivity = this.relatedEventOptions
				.filter((activity: PupilActivity | Lesson | LessonItem) => {
					const activityDate = getDateFromObject(activity);
					return activityDate && activityDate.getTime() < now.getTime();
				})
				.sort((a: PupilActivity | Lesson | LessonItem, b: PupilActivity | Lesson | LessonItem) => {
					const aDate = getDateFromObject(a);
					const bDate = getDateFromObject(b);
					if (!aDate || !bDate) {
						return 0;
					}
					return bDate.getTime() - aDate.getTime();
				})[0];

			if (recentActivity) {
				this.editableModel.relatedActivity = recentActivity;
			}
        },

        async selectActivity(activity: PupilActivity | Lesson | LessonItem | null) {
			if (!activity) {
				this.editableModel.relatedActivity = null;
				return;
			}

			this.relatedEventOptionsLoading = true;
			if (activity instanceof Lesson) {
				await this.lessonsStore.loadComplete(activity);
			} else if (activity instanceof LessonItem) {
				await this.lessonsStore.loadComplete(activity.lesson);
			}
			this.relatedEventOptionsLoading = false;

            this.editableModel.relatedActivity = activity;
        },

        async selectFile(file) {
            if (!file) {
                return;
            }

            this.editableModel.fileName = file.name;
            this.editableModel.fileContent = await Files.toBase64(file);
        },

		hideEditor() {
			UnsavedChanges.unobserveModel();

			this.observationsStore.clearErrors();

			// determine if this was a brand new observation or an edit
			const isNewObservation = !this.inUpdateMode;

			// only pass the saved observation up if it was a NEW creation.
			// if it was an edit (inUpdateMode), we pass null so the parent doesn't trigger the goal evaluation.
			const observationToEmit = (isNewObservation && this.savedObservation) ? this.savedObservation: null;

			this.$emit('requestClose', observationToEmit);
			this.savedObservation = null;
		},

		async confirmRemove() {

			if (this.deleting) {
				return;
			}
			this.deleting = true;

			try {
				await this.observationsStore.delete(this.originalModel);
				this.deleting = false;

				this.hideEditor();

				this.$notify({
					text: this.$t('The observation has been removed'),
					type: "success",
				});

			} catch (e: any) {
				if (!(e instanceof ApiErrorMessageBag)) {
					throw e;
				}
			}
		},

		async save() {

			if (!this.canSave) {
				return;
			}
			this.saving = true;

			try {
				await this.observationsStore.save(this.editableModel);

				this.saving = false;

				if (this.observation) {
					this.observation.setFromModel(this.editableModel);
					this.savedObservation = this.observation;
				} else {
					this.savedObservation = this.editableModel;
				}

				// Unobserve before triggering close so beforeClose check passes
				UnsavedChanges.unobserveModel();

				// Trigger animated close, hideEditor will fire after animation
				const drawer = this.$refs.drawer as any;
				if (drawer && drawer.prepareToClose) {
					drawer.prepareToClose();
				} else {
					this.hideEditor();
				}

			} catch (e) {
				// error message is populated now.
				this.saving = false;
				if (!(e instanceof ApiErrorMessageBag)) {
					throw e;
				}
			}
    	},

		filesDropped(files) {
			this.$refs.attachmentsContainer.filesDropped(files);
		},

		openEvaluationEditor(pupil: Pupil, goal: Goal) {
			this.evaluatedGoal = goal;
			this.evaluatedPupil = pupil;
		},

		closeEvaluationEditor() {
			this.evaluatedGoal = null;
		},

		canClose() {
			return UnsavedChanges.nonexistentOrIgnorable();
		},
  	},

}
</script>
